'use client'

import * as React from 'react'
import { type QueryItem, type QueryBarProps } from '../../index'
import Icon from '../../../Icon'
import { cn } from '../../../../../lib/utils'

type QueryBuilderProps = {
  items: QueryItem[]
  onChangeAction: (items: QueryItem[]) => void
  queryFields: QueryBarProps['queryFields']
  isActive: boolean
  onAnimationComplete?: () => void
  q?: string
  onQueryChange?: (next: string) => void
  relationshipPrefixes?: string[]
  fetchFieldsForPrefix?: (prefix: string) => Promise<
    Array<{
      type: 'chip'
      field: QueryBarProps['queryFields'][0]['field']
      comparison: QueryBarProps['queryFields'][0]['comparison']
    }>
  >
  alwaysShowSuggestions?: boolean
}

export type QueryBuilderHandle = {
  // Selects the first non-search suggestion; returns true if selection occurred
  selectFirstNonSearch: () => boolean
}

type FieldFilteredItem = {
  type: 'field'
  value: string
  label: string
  data: QueryBarProps['queryFields'][0]
  relevance: number
}
type PrefixFilteredItem = {
  type: 'prefix'
  value: string
  label: string
  data: { prefix: string }
  relevance: number
}
type OperatorFilteredItem = {
  type: 'operator'
  value: (typeof OPERATORS)[number]['type']
  label: string
  data: (typeof OPERATORS)[number]
  relevance: number
}
type SearchFilteredItem = {
  type: 'search'
  value: 'search'
  label: string
  data: { query: string }
  relevance: number
}
type _FilteredItem =
  | FieldFilteredItem
  | PrefixFilteredItem
  | OperatorFilteredItem
  | SearchFilteredItem

const OPERATORS = [
  { type: 'AND' as const, label: 'AND' },
  { type: 'OR' as const, label: 'OR' },
  { type: 'OPEN_PAREN' as const, label: '(' },
  { type: 'CLOSE_PAREN' as const, label: ')' },
] as const

export const QueryBuilder = React.forwardRef<
  QueryBuilderHandle,
  QueryBuilderProps
>(function QueryBuilder(
  {
    items,
    onChangeAction,
    queryFields,
    isActive,
    onAnimationComplete: _onAnimationComplete,
    q = '',
    onQueryChange,
    relationshipPrefixes,
    fetchFieldsForPrefix,
    alwaysShowSuggestions = false,
  },
  ref,
) {
  const suggestionsVisible = isActive || alwaysShowSuggestions
  const [extraByPrefix, setExtraByPrefix] = React.useState<
    Map<
      string,
      Array<{
        type: 'chip'
        field: QueryBarProps['queryFields'][0]['field']
        comparison: QueryBarProps['queryFields'][0]['comparison']
      }>
    >
  >(new Map())

  // Compute Levenshtein distance between two strings
  const levenshteinDistance = React.useCallback(
    (a: string, b: string): number => {
      if (a === b) return 0
      const aLen = a.length
      const bLen = b.length
      if (aLen === 0) return bLen
      if (bLen === 0) return aLen

      const v0 = new Array<number>(bLen + 1).fill(0)
      const v1 = new Array<number>(bLen + 1).fill(0)

      for (let i = 0; i <= bLen; i++) v0[i] = i

      for (let i = 0; i < aLen; i++) {
        v1[0] = i + 1
        const aChar = a.charCodeAt(i)
        for (let j = 0; j < bLen; j++) {
          const cost = aChar === b.charCodeAt(j) ? 0 : 1
          const min1 = (v1[j] ?? 0) + 1
          const min2 = (v0[j + 1] ?? 0) + 1
          const min3 = (v0[j] ?? 0) + cost
          v1[j + 1] = Math.min(min1, min2, min3)
        }
        for (let j = 0; j <= bLen; j++) v0[j] = v1[j] ?? 0
      }
      return v0[bLen] ?? 0
    },
    [],
  )

  // Convert distance to a 0..1 similarity score (1 is best)
  const similarityScore = React.useCallback(
    (a: string, b: string): number => {
      if (!a || !b) return 0
      if (a === b) return 1
      // Prefer prefix matches slightly
      if (a.startsWith(b))
        return Math.min(1, Math.max(0.9, b.length / a.length))
      const maxLen = Math.max(a.length, b.length)
      if (maxLen === 0) return 0
      const dist = levenshteinDistance(a, b)
      return (maxLen - dist) / maxLen
    },
    [levenshteinDistance],
  )

  const bestFieldSimilarity = React.useCallback(
    (label: string, value: string, searchTerm: string): number => {
      const labelL = label.toLowerCase()
      const valueL = value.toLowerCase()
      const qL = searchTerm.toLowerCase()

      const labelTokens = labelL.split(/[^a-z0-9]+/i).filter(Boolean)
      const valueTokens = valueL.split(/[^a-z0-9]+/i).filter(Boolean)

      let best = 0
      best = Math.max(best, similarityScore(labelL, qL))
      best = Math.max(best, similarityScore(valueL, qL))
      for (const t of labelTokens) best = Math.max(best, similarityScore(t, qL))
      for (const t of valueTokens) best = Math.max(best, similarityScore(t, qL))
      return best
    },
    [similarityScore],
  )

  // Filter fields and operators based on search query
  const filteredItems = React.useMemo(() => {
    const searchTerm = q.trim().toLowerCase()
    if (!searchTerm) {
      if (!alwaysShowSuggestions) {
        return []
      }

      return queryFields
        .map((field) => ({
          type: 'field' as const,
          value: String(field.field.value),
          label: field.field.label,
          data: field,
          relevance: 1,
        }))
        .slice(0, 5)
    }

    // Dot-path mode: when user types something like "employee." or "employee.co"
    const lastDotIndex = q.trim().lastIndexOf('.')
    const isDotPathMode = lastDotIndex > 0 // ensure there is at least one segment before '.'
    const dotPrefixLower = isDotPathMode
      ? q
          .trim()
          .slice(0, lastDotIndex + 1)
          .toLowerCase() // includes trailing '.'
      : ''
    const dotLeafQueryLower = isDotPathMode
      ? q
          .trim()
          .slice(lastDotIndex + 1)
          .toLowerCase()
      : ''

    // Convert fields and operators to a common format for filtering
    // 1) Relationship prefix suggestions
    const prefixItems = (() => {
      const out: PrefixFilteredItem[] = []
      if (!isDotPathMode) {
        // First-segment prefixes like "employee."
        const byFirstSeg = new Map<
          string,
          { modelLabel?: string; sampleLabel: string }
        >()
        const hasNestedByFirstSeg = new Map<string, boolean>()
        for (const f of queryFields) {
          const v = String(f.field.value)
          const parts = v.split('.')
          const seg0 = parts[0] || ''
          if (!seg0) continue
          if (!byFirstSeg.has(seg0)) {
            byFirstSeg.set(seg0, {
              modelLabel: f.field.modelLabel,
              sampleLabel: f.field.label,
            })
          }
          // Mark if there exists any field under seg0.<something>
          if (parts.length > 1) {
            hasNestedByFirstSeg.set(seg0, true)
          } else if (!hasNestedByFirstSeg.has(seg0)) {
            // initialize as false if not seen nested yet
            hasNestedByFirstSeg.set(seg0, false)
          }
        }
        // Augment from provided relationshipPrefixes if any
        if (Array.isArray(relationshipPrefixes)) {
          for (const p of relationshipPrefixes) {
            const seg = String(p).split('.')[0] || ''
            if (!seg) continue
            hasNestedByFirstSeg.set(seg, true)
            if (!byFirstSeg.has(seg)) {
              byFirstSeg.set(seg, { modelLabel: undefined, sampleLabel: seg })
            }
          }
        }
        for (const [seg, meta] of Array.from(byFirstSeg.entries())) {
          // Only show prefix chip when this first segment actually has nested fields
          if (!hasNestedByFirstSeg.get(seg)) continue
          const display =
            (meta.modelLabel && meta.modelLabel.trim()) || seg.trim()
          const dispLower = display.toLowerCase()
          if (
            dispLower.includes(searchTerm) ||
            seg.toLowerCase().includes(searchTerm)
          ) {
            const label = `${display}...`
            const relevance = Math.max(
              bestFieldSimilarity(display, seg, searchTerm),
              bestFieldSimilarity(display, display, searchTerm),
            )
            out.push({
              type: 'prefix',
              value: `${seg}.`,
              label,
              data: { prefix: `${seg}.` },
              relevance: Math.max(1, relevance + 0.1), // nudge above plain fields
            })
          }
        }
      } else {
        // Next-segment prefixes like "employee.company."
        const typedPrefix = dotPrefixLower // already lowercased, ends with '.'
        const basePrefixRaw = q.trim().slice(0, lastDotIndex + 1) // keep original casing for display
        const nextSegs = new Set<string>()
        const nextSegHasNested = new Map<string, boolean>()
        for (const f of queryFields) {
          const v = String(f.field.value)
          const vLower = v.toLowerCase()
          if (!vLower.startsWith(typedPrefix)) continue
          const parts = v.split('.')
          const typedCount = basePrefixRaw.split('.').filter(Boolean).length
          const next = parts[typedCount] // segment right after typed prefix
          if (next) {
            nextSegs.add(next)
            // If there are more parts after 'next', it's a relationship segment with nested fields
            if (parts.length > typedCount + 1) {
              nextSegHasNested.set(next, true)
            } else if (!nextSegHasNested.has(next)) {
              nextSegHasNested.set(next, false)
            }
          }
        }
        // Augment from provided relationshipPrefixes for deeper hops
        if (Array.isArray(relationshipPrefixes)) {
          const baseLower = basePrefixRaw.toLowerCase()
          const typedCount = basePrefixRaw.split('.').filter(Boolean).length
          for (const p of relationshipPrefixes) {
            const pLower = String(p).toLowerCase()
            if (!pLower.startsWith(baseLower)) continue
            const parts = String(p).split('.')
            const next = parts[typedCount]
            if (next) {
              nextSegs.add(next)
              nextSegHasNested.set(next, true)
            }
          }
        }
        for (const seg of Array.from(nextSegs.values())) {
          // Only show prefix chip when there are deeper nested fields under this next segment
          if (!nextSegHasNested.get(seg)) continue
          const disp = seg
            .split(/[-_]/)
            .map((s: string) => s.charAt(0).toUpperCase() + s.slice(1))
            .join(' ')
          const label = `${disp}...`
          const relevance =
            dotLeafQueryLower.length > 0
              ? bestFieldSimilarity(disp, seg, dotLeafQueryLower)
              : 1.1
          out.push({
            type: 'prefix',
            value: `${basePrefixRaw}${seg}.`,
            label,
            data: { prefix: `${basePrefixRaw}${seg}.` },
            relevance,
          })
        }
      }
      return out
    })()

    // 2) Field items (leaf fields)
    const allQueryFields =
      isDotPathMode && extraByPrefix.has(q.trim().slice(0, lastDotIndex + 1))
        ? [
            ...queryFields,
            ...(extraByPrefix.get(q.trim().slice(0, lastDotIndex + 1)) || []),
          ]
        : queryFields
    const fieldItems: FieldFilteredItem[] = allQueryFields.flatMap((field) => {
      const value = String(field.field.value)
      const valueLower = value.toLowerCase()
      const label = field.field.label

      if (isDotPathMode) {
        // Only include fields that are under the typed relationship prefix
        if (!valueLower.startsWith(dotPrefixLower)) {
          return [] as FieldFilteredItem[]
        }
        const leafValue = value.split('.').pop() ?? ''
        const leafLabel = label
        const relevance =
          dotLeafQueryLower.length > 0
            ? bestFieldSimilarity(leafLabel, leafValue, dotLeafQueryLower)
            : 1
        return [
          {
            type: 'field' as const,
            value,
            label: leafLabel,
            data: field,
            relevance,
          },
        ]
      }

      const relevance = searchTerm
        ? bestFieldSimilarity(label, value, searchTerm)
        : 1
      return [
        {
          type: 'field' as const,
          value,
          label,
          data: field,
          relevance,
        },
      ]
    })

    // 3) Operators (hidden in dot-path mode)
    const operatorItems: OperatorFilteredItem[] = isDotPathMode
      ? []
      : OPERATORS.map((op) => ({
          type: 'operator' as const,
          value: op.type,
          label: op.label,
          data: op,
          relevance: searchTerm
            ? similarityScore(op.label.toLowerCase(), searchTerm)
            : 1,
        }))

    // 4) Search query chip if q is not empty (hidden in dot-path mode)
    const searchQueryItems: SearchFilteredItem[] =
      q && !isDotPathMode
        ? [
            {
              type: 'search' as const,
              value: 'search',
              label: `"${q}"`,
              data: { query: q },
              relevance: 2, // Higher relevance to appear first
            },
          ]
        : []

    // Merge, filter by relevance, and limit to 5 items
    return (
      [
        ...searchQueryItems,
        ...prefixItems,
        ...fieldItems,
        ...operatorItems,
      ] as _FilteredItem[]
    )
      .filter((item) => !searchTerm || item.relevance > 0)
      .sort((a, b) => {
        if (b.relevance !== a.relevance) return b.relevance - a.relevance
        // Tie-breaker: shorter label first, then lexicographic
        if (a.label.length !== b.label.length)
          return a.label.length - b.label.length
        return a.label.localeCompare(b.label)
      })
      .slice(0, 5)
  }, [
    queryFields,
    q,
    bestFieldSimilarity,
    similarityScore,
    alwaysShowSuggestions,
    relationshipPrefixes,
    extraByPrefix,
  ])

  // On dot-path mode, lazily fetch fields for the typed prefix if none are present yet
  React.useEffect(() => {
    const trimmed = q.trim()
    const lastDotIndex = trimmed.lastIndexOf('.')
    const isDotPathMode = lastDotIndex > 0

    if (!isDotPathMode || !fetchFieldsForPrefix) return
    const basePrefixRaw = trimmed.slice(0, lastDotIndex + 1)
    // Avoid refetching if we already have extras for this prefix
    if (extraByPrefix.has(basePrefixRaw)) return
    // If existing queryFields already contain values under this prefix, skip fetch
    const lowerPref = basePrefixRaw.toLowerCase()
    const hasAny = queryFields.some((f) =>
      String(f.field.value).toLowerCase().startsWith(lowerPref),
    )
    if (hasAny) return
    let cancelled = false
    ;(async () => {
      try {
        const extras = await fetchFieldsForPrefix(basePrefixRaw)
        if (cancelled) return
        setExtraByPrefix((prev) => {
          const next = new Map(prev)
          next.set(basePrefixRaw, extras)
          return next
        })
      } catch {
        // ignore fetch errors
      }
    })()
    return () => {
      cancelled = true
    }
  }, [q, queryFields, fetchFieldsForPrefix, extraByPrefix])

  const handleSelectItem = React.useCallback(
    (item: _FilteredItem) => {
      if (item.type === 'prefix') {
        onQueryChange?.(item.data.prefix)
        return true
      }
      const lastItem = items[items.length - 1]
      const needsOperator =
        lastItem &&
        lastItem.type !== 'expression' &&
        (item.type === 'field' || item.type === 'search')
      const newItem: QueryItem =
        item.type === 'field'
          ? {
              id: Math.random().toString(36).substring(7),
              type: 'chip',
              data: {
                field: item.data.field,
                comparison: item.data.comparison,
              },
            }
          : item.type === 'search'
            ? {
                id: Math.random().toString(36).substring(7),
                type: 'search',
                data: {
                  value: item.data.query,
                },
              }
            : {
                id: Math.random().toString(36).substring(7),
                type: 'expression',
                data: {
                  expressionType: item.data.type,
                },
              }
      if (needsOperator) {
        const andOperator: QueryItem = {
          id: Math.random().toString(36).substring(7),
          type: 'expression',
          data: {
            expressionType: 'AND',
          },
        }
        onChangeAction([...items, andOperator, newItem])
      } else {
        onChangeAction([...items, newItem])
      }
      return true
    },
    [items, onChangeAction, onQueryChange],
  )

  React.useImperativeHandle(
    ref,
    () => ({
      selectFirstNonSearch: () => {
        if (!Array.isArray(filteredItems) || filteredItems.length === 0) {
          return false
        }
        const candidate =
          filteredItems.find((it) => it.type !== 'search') ||
          filteredItems.find((it) => it.type === 'search')
        if (!candidate) return false
        return handleSelectItem(candidate)
      },
    }),
    [filteredItems, handleSelectItem],
  )

  return (
    <div
      aria-hidden={!suggestionsVisible}
      className={cn(
        'flex flex-wrap gap-x-1 gap-y-3 w-full bg-background pb-1 -mx-4 px-4 transform-gpu origin-top will-change-transform overflow-hidden transition-[transform,opacity,margin] duration-300 ease-out',
        suggestionsVisible
          ? 'mt-2 opacity-100 scale-y-100'
          : 'mt-0 opacity-0 scale-y-95 pointer-events-none h-0',
      )}
    >
      {(() => {
        const items = filteredItems || []
        let firstNonSearchIndex = -1
        for (let i = 0; i < items.length; i += 1) {
          if (items[i]?.type !== 'search') {
            firstNonSearchIndex = i
            break
          }
        }
        return items.map((item, idx) => (
          <button
            key={item.value}
            data-id={item.value}
            onClick={() => {
              void handleSelectItem(item)
            }}
            className="inline-flex items-center justify-center px-1 bg-accent/20 hover:bg-accent/80 rounded-md text-2xs transition-colors text-foreground/50 hover:text-foreground"
          >
            {item.type === 'operator' && item.data.type === 'OPEN_PAREN' ? (
              '('
            ) : item.type === 'operator' && item.data.type === 'CLOSE_PAREN' ? (
              ')'
            ) : item.type === 'search' ? (
              <>
                {`Search for "${item.data.query}"`}
                <span className="ml-1 inline-flex items-center gap-1 text-2xs text-muted-foreground border rounded px-1 shadow">
                  <Icon name="CornerDownLeft" size={10} />
                  Enter
                </span>
              </>
            ) : item.type === 'field' ? (
              <span className="flex items-center gap-1">
                {item.data.field.showModelLabel &&
                  typeof item.data.field.value === 'string' &&
                  !item.data.field.value.includes('.') &&
                  item.data.field.modelLabel &&
                  item.data.field.modelLabel !== item.data.field.label && (
                    <>
                      <span className="text-2xs text-muted-foreground/70">
                        {item.data.field.modelLabel}
                      </span>
                      <span className="text-2xs text-muted-foreground/50">
                        •
                      </span>
                    </>
                  )}
                <span>{item.label}</span>
                {idx === firstNonSearchIndex && (
                  <span className="ml-1 inline-flex items-center gap-1 text-2xs text-muted-foreground border rounded px-1 shadow">
                    Tab
                  </span>
                )}
              </span>
            ) : (
              <>
                {item.label}
                {idx === firstNonSearchIndex && (
                  <span className="ml-1 text-2xs text-muted-foreground border  shadow rounded px-1">
                    Tab
                  </span>
                )}
              </>
            )}
          </button>
        ))
      })()}
    </div>
  )
})
